import Quill from 'quill/core';
import React, { useCallback, useEffect, useState } from 'react';
import { useThrottledCallback } from 'use-debounce';
import useAppSelector from '../../app/redux/useAppSelector';
import { useQuill } from '../../editor/QuillContext';
import BasicHeader from '../../editor/formats/BasicHeader';
import findClosestScrollingContainer from '../../editor/utils/findClosestScrollingContainer';

export type TOCHeader = {
  id: string;
  text: string;
  level: number;
};

export type TOCState = {
  activeH1: string | null;
  activeH2: string | null;
  activeH3: string | null;
  headers: TOCHeader[];
};

export const getHeadersForTOC = (quill: Quill) => {
  const headers: TOCHeader[] = [];

  quill.scroll.descendants(BasicHeader).forEach((header) => {
    const id = header.getAnchorId();
    if (id.length > 0 && header.getText() !== '') {
      headers.push({
        id: header.getAnchorId(),
        text: header.getText(),
        level: header.statics.formats(header.domNode),
      });
    }
  });

  return headers;
};

interface ConnectedTableOfContentsProps {
  children: (state: TOCState) => React.ReactNode;
  postId: string;
}

const ConnectedTableOfContents = ({
  children,
  postId,
}: ConnectedTableOfContentsProps) => {
  const version = useAppSelector((state) => state.editor.version);
  const { quill } = useQuill();
  const [state, setState] = useState<TOCState>({
    activeH1: null,
    activeH2: null,
    activeH3: null,
    headers: [],
  });

  const onScroll = useThrottledCallback(
    ({ offsetTop }: { offsetTop: number }) => {
      let activeH1: string | null = null;
      let activeH2: string | null = null;
      let activeH3: string | null = null;

      state.headers.some(({ id }) => {
        const node = quill?.root.querySelector(BasicHeader.getDOMSelector(id));
        if (!node) return false;
        if (node.getBoundingClientRect().top - offsetTop > 0) {
          return true;
        }
        if (node.tagName === 'H1') {
          activeH1 = id;
          activeH2 = null;
          activeH3 = null;
        } else if (node.tagName === 'H2') {
          activeH2 = id;
          activeH3 = null;
        } else if (node.tagName === 'H3') {
          activeH3 = id;
        }
        return false;
      });

      setState((s) => ({ ...s, activeH1, activeH2, activeH3 }));
    },
    100,
  );

  const generateHeaders = useCallback(() => {
    if (quill) {
      setState((s) => ({ ...s, headers: getHeadersForTOC(quill) }));
    }
  }, [quill, setState]);

  const throttledGenerateHeaders = useThrottledCallback(generateHeaders, 2000);

  useEffect(() => {
    const scrollingContainer =
      quill?.root && findClosestScrollingContainer(quill.root);
    if (!scrollingContainer) return undefined;

    const listener = () => onScroll(scrollingContainer);
    scrollingContainer.addEventListener('scroll', listener);
    return () => {
      scrollingContainer.removeEventListener('scroll', listener);
      throttledGenerateHeaders.cancel();
    };
  }, [quill, onScroll, throttledGenerateHeaders]);

  useEffect(() => {
    if (quill) {
      // new post loaded
      generateHeaders();

      quill.once(Quill.events.TEXT_CHANGE, () => generateHeaders());
    }
  }, [quill, postId, generateHeaders]);

  useEffect(() => {
    if (quill) {
      throttledGenerateHeaders();
    }
  }, [quill, version, throttledGenerateHeaders]);

  return <>{children(state)}</>;
};

export default ConnectedTableOfContents;
